<script setup lang="ts">
import { computed } from 'vue';
import { StoryblokRichText } from '@storyblok/vue';
import { resolveStoryblokStringOption } from '@/utils/storyblokOption';

interface ImageTextBlok {
    _uid: string;
    image: { filename: string; alt: string };
    headline: string;
    content: object;
    cta_text: string;
    cta_link: { cached_url: string; url: string };
    image_position: string;
    background_color: string;
    mobile_image_focus?: 'left' | 'center' | 'right' | string | { value: string };
    /** Matches GiftSection “download ads” + checklist row on the homepage */
    layout?: 'default' | 'voucher_promo' | string | { value: string };
}

const props = defineProps<{ blok: ImageTextBlok }>();

/** Same assets as GiftSection.vue (Storyblok often drops local `image.filename` on save). */
const PROMO_IMG_PNG = '/images/redesign/download_ads.png';
const PROMO_IMG_WEBP = '/images/redesign/download_ads.webp';

function resolvedLayout(layout: ImageTextBlok['layout']): string {
    if (layout == null || layout === '') {
        return '';
    }
    if (typeof layout === 'string') {
        return layout;
    }
    if (typeof layout === 'object' && layout !== null && 'value' in layout && typeof (layout as { value: string }).value === 'string') {
        return (layout as { value: string }).value;
    }

    return String(layout);
}

/** When `layout` is missing after CMS save: same shape as Muttertag promo (intro + checklist, no headline). */
const PROMO_DOC_BLOCK_TYPES = new Set(['paragraph', 'bullet_list', 'heading']);

function docMatchesPromoRichTextFallback(content: unknown): boolean {
    if (!content || typeof content !== 'object') {
        return false;
    }
    const doc = content as { type?: string; content?: { type?: string }[] };
    if (doc.type !== 'doc' || !Array.isArray(doc.content) || doc.content.length === 0) {
        return false;
    }
    const hasBulletList = doc.content.some((node) => node?.type === 'bullet_list');
    if (!hasBulletList) {
        return false;
    }

    return doc.content.every((node) => Boolean(node?.type && PROMO_DOC_BLOCK_TYPES.has(node.type)));
}

const isVoucherPromo = computed(() => {
    const layout = resolvedLayout(props.blok.layout).toLowerCase();
    if (layout === 'voucher_promo' || layout.includes('voucher')) {
        return true;
    }
    const fn = (props.blok.image?.filename ?? '').toLowerCase();
    if (fn.includes('download_ads')) {
        return true;
    }
    if (!props.blok.headline && docMatchesPromoRichTextFallback(props.blok.content)) {
        return true;
    }

    return false;
});

/** Storyblok Image Service only applies to assets on storyblok.com */
const resolvedImageSrc = computed(() => {
    const filename = props.blok.image?.filename;
    if (!filename) {
        return '';
    }
    return filename.includes('storyblok.com') ? `${filename}/m/800x600` : filename;
});

const imageObjectPositionClass = computed(() => {
    const f = resolveStoryblokStringOption(props.blok.mobile_image_focus).toLowerCase();
    if (f === 'left') {
        return 'object-left lg:object-center';
    }
    if (f === 'right') {
        return 'object-right lg:object-center';
    }

    return 'object-center';
});

/**
 * Split the richtext doc into two parts for promo layout:
 *   introDoc  = paragraphs / headings before the first bullet_list
 *   listDoc   = bullet_list (and anything after)
 */
const introDoc = computed(() => {
    const doc = props.blok.content as { type?: string; content?: any[] } | undefined;
    if (!doc?.content) {
        return null;
    }
    const idx = doc.content.findIndex((n: any) => n?.type === 'bullet_list');
    if (idx <= 0) {
        return null;
    }
    return { type: 'doc', content: doc.content.slice(0, idx) };
});

const listDoc = computed(() => {
    const doc = props.blok.content as { type?: string; content?: any[] } | undefined;
    if (!doc?.content) {
        return null;
    }
    const idx = doc.content.findIndex((n: any) => n?.type === 'bullet_list');
    if (idx < 0) {
        return doc;
    }
    return { type: 'doc', content: doc.content.slice(idx) };
});

function resolveLink(link: { cached_url: string; url: string } | undefined): string {
    return link?.cached_url || link?.url || '#';
}
</script>

<template>
    <!-- Voucher promo: headline + intro full-width, then image | checklist + CTA side-by-side -->
    <section
        v-if="isVoucherPromo"
        v-editable="blok"
        class="bg-white py-12 xl:py-20"
    >
        <div class="mx-auto flex max-w-[1292px] flex-col items-center px-4 text-center">
            <!-- Headline -->
            <h2
                v-if="blok.headline"
                class="mb-6 text-[28px] font-semibold leading-tight text-[#004646] xl:mb-8 xl:text-4xl"
            >
                {{ blok.headline }}
            </h2>

            <!-- Intro paragraphs (centered, above the image row) -->
            <div v-if="introDoc" class="voucher-promo-richtext mb-8 max-w-3xl text-[#393734] xl:mb-10">
                <StoryblokRichText :doc="introDoc" />
            </div>

            <!-- Image + Checklist row (centered as a pair) -->
            <div class="flex w-full flex-col items-center gap-10 md:flex-row md:items-center md:justify-center md:gap-12 xl:gap-20">
                <div class="relative grid shrink-0 place-items-center xl:h-[241px]">
                    <div class="absolute h-[124px] w-[124px] rounded-full bg-[#F3A073] blur-xl xl:h-[148px] xl:w-[148px]" />
                    <picture>
                        <source :srcset="PROMO_IMG_WEBP" type="image/webp" />
                        <img
                            class="relative z-[1] h-[241px] w-[241px]"
                            :src="PROMO_IMG_PNG"
                            :alt="blok.image?.alt || 'GLOWCARD Gutschein'"
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>
                </div>

                <div
                    class="mx-auto flex w-full max-w-[342px] flex-col items-stretch text-left md:mx-0 md:max-w-[420px] md:items-start"
                >
                    <div v-if="listDoc" class="voucher-promo-richtext w-full text-[#393734]">
                        <StoryblokRichText :doc="listDoc" />
                    </div>
                    <a
                        v-if="blok.cta_text"
                        :href="resolveLink(blok.cta_link)"
                        class="mt-8 inline-flex h-14 w-full items-center justify-center rounded-md bg-[#F08E59] px-8 py-4 text-lg font-semibold text-white shadow-orange-button transition hover:opacity-90 xl:mt-10 xl:w-auto"
                    >
                        {{ blok.cta_text }}
                    </a>
                </div>
            </div>
        </div>
    </section>

    <section
        v-else
        v-editable="blok"
        class="py-12 xl:py-20"
        :class="blok.background_color === 'warm' ? 'bg-[#FCEEE6]' : blok.background_color === 'light' ? 'bg-[#FCF7F4]' : 'bg-white'"
    >
        <div
            class="mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-4 md:flex-row md:gap-12 xl:gap-20"
            :class="{ 'md:flex-row-reverse': blok.image_position === 'right' }"
        >
            <div class="w-full md:w-1/2">
                <img
                    v-if="blok.image?.filename"
                    :src="resolvedImageSrc"
                    :alt="blok.image.alt || blok.headline"
                    class="h-auto w-full rounded-2xl object-cover"
                    :class="imageObjectPositionClass"
                    loading="lazy"
                />
            </div>

            <div class="w-full text-center md:w-1/2 md:text-left">
                <h2 v-if="blok.headline" class="mb-4 text-[28px] font-semibold leading-tight text-[#004646] xl:mb-6 xl:text-4xl">
                    {{ blok.headline }}
                </h2>
                <div class="prose prose-lg max-w-none text-[#393734]">
                    <StoryblokRichText v-if="blok.content" :doc="blok.content" />
                </div>
                <a
                    v-if="blok.cta_text"
                    :href="resolveLink(blok.cta_link)"
                    class="mt-6 inline-block rounded-lg bg-[#F08E59] px-8 py-4 text-lg font-semibold text-white shadow-orange-button transition hover:opacity-90 xl:mt-8"
                >
                    {{ blok.cta_text }}
                </a>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* Intro copy above the checklist */
.voucher-promo-richtext :deep(p) {
    margin: 0 0 1rem;
    font-size: 1.125rem;
    line-height: 1.5rem;
    color: #393734;
}

@media (min-width: 1280px) {
    .voucher-promo-richtext :deep(p) {
        font-size: 1.25rem;
        line-height: 1.75rem;
    }
}

/* Checklist like the reference (teal check) — override typography / Storyblok defaults */
.voucher-promo-richtext :deep(ul),
.voucher-promo-richtext :deep(ol) {
    margin: 1.25rem 0 0 !important;
    padding: 0 !important;
    list-style: none !important;
    list-style-type: none !important;
}

.voucher-promo-richtext :deep(ul > li),
.voucher-promo-richtext :deep(ol > li) {
    position: relative;
    margin-bottom: 1rem;
    padding-left: 2.5rem;
    font-size: 1.125rem;
    line-height: 1.5rem;
    color: #393734;
    list-style: none !important;
    list-style-type: none !important;
}

@media (min-width: 1280px) {
    .voucher-promo-richtext :deep(ul > li),
    .voucher-promo-richtext :deep(ol > li) {
        margin-bottom: 1.25rem;
        font-size: 1.25rem;
        line-height: 1.75rem;
    }
}

.voucher-promo-richtext :deep(ul > li::before),
.voucher-promo-richtext :deep(ol > li::before) {
    content: '';
    position: absolute;
    left: 0;
    top: 0.2em;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 9999px;
    background-color: rgba(38, 132, 57, 0.12);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23268439' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6 9 17l-5-5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 0.95rem 0.95rem;
}

.voucher-promo-richtext :deep(ul > li:last-child),
.voucher-promo-richtext :deep(ol > li:last-child) {
    margin-bottom: 0;
}

.voucher-promo-richtext :deep(li > p) {
    margin: 0;
}
</style>
